import { useMemo, useState } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'

import type { ErrorLike } from '@apollo/client'
import clsx from 'clsx'
import { format } from 'date-fns'

import { useSplitEnabled } from '@buffer-mono/features'
import {
  CarouselIcon,
  Flex,
  PdfIcon,
  PlayFilledIcon,
  Skeleton,
  Table,
  Text,
  ToggleGroup,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { MediaMarker } from '~publish/components/MediaMarker/MediaMarker'
import { PostTypeBadge } from '~publish/components/PostType'
import { getProxiedThumbnailUrl } from '~publish/legacy/utils/bufferImages'

import type { SortDirection, TopPostsSortBy } from '../../graphqlEnums'
import { TopPostActions } from './TopPostActions'
import type { TopPost } from '../../hooks'
import { useColumnVisibility } from '../../hooks'
import {
  formatDateRange,
  getLinkedInBoostUrl,
  getNoPostsDescription,
  isMediaOnlyPost,
  MEDIA_ONLY_CAPTION,
} from '../../utils'
import { ChartErrorState } from '../ChartErrorState'
import { ColumnPickerPopover } from '../ColumnPickerPopover'
import { MetricNumber } from '../MetricNumber'
import { NeverSyncedEmptyState } from '../NeverSyncedEmptyState'
import { NoDataEmptyState } from '../NoDataEmptyState'
import { PaginationControls } from '../PaginationControls'
import { SectionCard } from '../SectionCard'
import { SortableHeaderCell } from '../SortableHeaderCell'

import sharedStyles from '../chart-shared.module.css'
import styles from './TopPosts.module.css'

/** Metrics that support server-side sorting via TopPostsSortBy.
 *  A metric is sortable on every network that reports it; the sortable header
 *  only renders for the columns a channel actually shows. */
const SORTABLE_METRICS = new Set<string>([
  'engagementRate',
  'reactions',
  'comments',
  'views',
  'impressions',
  'reach',
  'shares',
  'reposts',
  'clicks',
  'saves',
  'follows',
  'quotes',
  'totalTimeWatched',
  'averageTimeWatched',
  'viewers',
])

interface TopPostsProps {
  channelId: string
  /** When false, hides the composer-opening row actions (see TopPostActions). */
  canCreatePosts?: boolean
  topPosts: TopPost[]
  loading: boolean
  refreshing?: boolean
  error: ErrorLike | undefined
  page: number
  perPage: number
  totalCount: number
  onPageChange: (page: number) => void
  sortBy: TopPostsSortBy
  sortDirection: SortDirection
  onSortChange: (field: TopPostsSortBy, direction: SortDirection) => void
  startDate: Date
  endDate: Date
  /** When set, enables the comparison tab and length-mismatch handling. */
  comparisonStartDate?: Date | null
  comparisonEndDate?: Date | null
  selectedDateRange?: string
  hasNeverSynced?: boolean
  /**
   * Which period's posts are being shown. The section subtitle renders the
   * date range of the active period. Defaults to 'primary'.
   */
  period?: 'primary' | 'comparison'
  /** Called when the user picks a tab. Required when comparisonLabel is set. */
  onPeriodChange?: (period: 'primary' | 'comparison') => void
  /** Label for the comparison tab (e.g. "Last Year"). When omitted, the tab toggle is hidden. */
  comparisonLabel?: string
  /** Abbreviated comparison label used on narrow cards, where the toggle is
   * full-width and the long label would otherwise wrap. Falls back to
   * `comparisonLabel`. */
  comparisonLabelShort?: string
  /** Whether a tag filter is currently narrowing the results. When true, the
   * no-posts empty state offers a "clear the tag filter" link. */
  hasTagFilter?: boolean
  /** Clears the active tag filter, resetting the page to its default (all tags). */
  onClearTagFilter?: () => void
  /** Whether a post type filter is currently narrowing the results. When true,
   * the no-posts empty state offers a "clear the post type filter" link. */
  hasPostTypeFilter?: boolean
  /** Clears the active post type filter, resetting to all formats. */
  onClearPostTypeFilter?: () => void
  /** Clears the tag and post type filters together, in one navigation. Used
   * when both are active. @see NoDataEmptyState */
  onClearAllFilters?: () => void
}

function isTopPostsSortBy(key: string): key is TopPostsSortBy {
  return SORTABLE_METRICS.has(key)
}

function renderMetricValue(value: number, metricType: string): ReactNode {
  if (metricType === 'engagementRate') {
    return `${value.toFixed(2)}%`
  }
  return <MetricNumber value={value} />
}

function getPostMetricValue(post: TopPost, metricType: string): ReactNode {
  const metric = post.metrics?.find((m) => m.type === metricType)
  if (!metric) return '-'
  return renderMetricValue(metric.value, metricType)
}

function TopPostsTableSkeleton({
  rowCount,
  columnCount,
}: {
  rowCount: number
  columnCount: number
}): JSX.Element {
  return (
    <Table>
      <Table.Header className={styles.tableHeader}>
        <Table.Row>
          <Table.HeaderCell align="left">Posts</Table.HeaderCell>
          {Array.from({ length: columnCount }, (_, i) => (
            <Table.HeaderCell key={i} align="left">
              <Skeleton width={70} height={16} />
            </Table.HeaderCell>
          ))}
          <Table.HeaderCell align="left" className={styles.actionsColumn}>
            Actions
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {Array.from({ length: rowCount }, (_, i) => (
          <Table.Row key={i}>
            <Table.Cell>
              <Flex gap="sm" align="center">
                <Skeleton width={24} height={16} />
                <Skeleton width={48} height={48} />
                <Skeleton width={200} height={16} />
              </Flex>
            </Table.Cell>
            {Array.from({ length: columnCount }, (_columnValue, j) => (
              <Table.Cell key={j}>
                <Skeleton width={50} height={16} />
              </Table.Cell>
            ))}
            <Table.Cell className={styles.actionsColumn} />
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  )
}

type TopPostAsset = TopPost['assets'][number]

/**
 * Sized down from the 16px default: the marker it sits in is `--space-lg`, which
 * is as small as 16px on a narrow viewport, so a default icon fills the whole
 * badge.
 */
function getAssetIcon(asset: TopPostAsset): JSX.Element | null {
  if (asset.type === 'video') return <PlayFilledIcon size="xsmall" />
  if (asset.type === 'document') return <PdfIcon size="xsmall" />
  return null
}

function getMediaMarker(post: TopPost): JSX.Element | null {
  const assets = post.assets ?? []

  if (assets.length > 1) {
    return (
      <MediaMarker className={styles.thumbnailBadge}>
        <CarouselIcon size="xsmall" />
        <VisuallyHidden>Multiple images</VisuallyHidden>
      </MediaMarker>
    )
  }

  const displayAsset = assets.find((asset) => asset.thumbnail) ?? assets[0]
  const icon = displayAsset ? getAssetIcon(displayAsset) : null
  if (!icon) return null

  return (
    <MediaMarker className={styles.thumbnailBadge} aria-hidden>
      {icon}
    </MediaMarker>
  )
}

function PostThumbnail({ asset }: { asset: TopPostAsset }): JSX.Element {
  const [imageError, setImageError] = useState(false)

  if (!asset.thumbnail || imageError) {
    return <div className={styles.thumbnailPlaceholder} />
  }

  return (
    <img
      src={getProxiedThumbnailUrl(asset.thumbnail)}
      alt=""
      className={styles.thumbnail}
      onError={() => setImageError(true)}
    />
  )
}

function TopPostRow({
  post,
  rank,
  channelId,
  canCreatePosts,
  metricColumns,
}: {
  post: TopPost
  rank: number
  channelId: string
  canCreatePosts: boolean
  metricColumns: string[]
}): JSX.Element {
  const assetForDisplay =
    post.assets?.find((a) => a.thumbnail) ?? post.assets?.[0]
  const mediaMarker = getMediaMarker(post)
  const { search } = useLocation()
  const { isEnabled: isBoostEnabled } = useSplitEnabled(
    'geid-linkedin-boost-ctas',
  )

  const boostUrl =
    isBoostEnabled && post.channelService === 'linkedin' && rank <= 3
      ? getLinkedInBoostUrl(post.externalLink, post.channel?.serviceId)
      : null

  return (
    <Table.Row className={styles.postRow}>
      <Table.Cell className={styles.postCell}>
        <Flex gap="sm" align="center">
          <Text color="subtle" size="sm" className={styles.rank}>
            #{rank}
          </Text>
          <Link
            to={`posts/${post.id}${search}`}
            className={styles.stretchedLink}
            {...(!post.text && { 'aria-label': 'View post details' })}
          >
            {assetForDisplay && (
              <div className={styles.thumbnailWrapper}>
                <PostThumbnail asset={assetForDisplay} />
                {mediaMarker}
              </div>
            )}
            <Flex direction="column" gap="2xs">
              {post.text?.trim() ? (
                <Text className={styles.postText} translate="no">
                  {post.text}
                </Text>
              ) : (
                isMediaOnlyPost(post) && (
                  <Text className={styles.postText} color="subtle">
                    {MEDIA_ONLY_CAPTION}
                  </Text>
                )
              )}
              <Flex align="center" gap="xs">
                {post.sentAt && (
                  <Text size="sm" color="subtle">
                    {format(new Date(post.sentAt), 'MMM d, yyyy')}
                  </Text>
                )}
                <PostTypeBadge type={post.metadata?.type} size="small" />
              </Flex>
            </Flex>
          </Link>
        </Flex>
      </Table.Cell>

      {metricColumns.map((type) => (
        <Table.Cell className={styles.postCell} key={type} align="left">
          {getPostMetricValue(post, type)}
        </Table.Cell>
      ))}

      <Table.Cell
        align="left"
        className={clsx(styles.actionsColumn, styles.postCell)}
      >
        <TopPostActions
          postId={post.id}
          channelId={channelId}
          canCreatePosts={canCreatePosts}
          externalLink={post.externalLink}
          allowedActions={post.allowedActions ?? []}
          boostUrl={boostUrl}
          postType={post.metadata?.type}
          sentAt={post.sentAt}
          ctas={{
            shareLink: 'publish-insights-topPosts-shareLink-1',
            duplicate: 'publish-insights-topPosts-duplicate-1',
          }}
        />
      </Table.Cell>
    </Table.Row>
  )
}

function TopPostsTable({
  posts,
  channelId,
  canCreatePosts,
  rankOffset,
  totalCount,
  sortBy,
  sortDirection,
  onSortChange,
  visibleMetrics,
  availableMetrics,
  selectedMetrics,
  toggleMetric,
  reorderMetric,
  showColumnSelector,
}: {
  posts: TopPost[]
  channelId: string
  canCreatePosts: boolean
  rankOffset: number
  totalCount: number
  sortBy: TopPostsSortBy
  sortDirection: SortDirection
  onSortChange: (field: TopPostsSortBy, direction: SortDirection) => void
  visibleMetrics: Array<{ type: string; displayName: string }>
  availableMetrics: Array<{ type: string; displayName: string }>
  selectedMetrics: ReadonlySet<string>
  toggleMetric: (type: string) => void
  reorderMetric: (activeType: string, overType: string) => void
  showColumnSelector: boolean
}): JSX.Element {
  function handleSort(key: string): void {
    if (key === sortBy) {
      onSortChange(key, sortDirection === 'desc' ? 'asc' : 'desc')
    } else if (isTopPostsSortBy(key)) {
      onSortChange(key, 'desc')
    }
  }

  return (
    <Table className={styles.table}>
      <Table.Header className={styles.tableHeader}>
        <Table.Row>
          <Table.HeaderCell align="left">Posts · {totalCount}</Table.HeaderCell>
          {visibleMetrics.map(({ type, displayName }) =>
            SORTABLE_METRICS.has(type) ? (
              <SortableHeaderCell
                key={type}
                sortKey={type}
                label={displayName}
                activeSortKey={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            ) : (
              <Table.HeaderCell key={type} align="left">
                {displayName}
              </Table.HeaderCell>
            ),
          )}
          <Table.HeaderCell align="left" className={styles.actionsColumn}>
            {showColumnSelector ? (
              <ColumnPickerPopover
                availableMetrics={availableMetrics}
                selectedMetrics={selectedMetrics}
                onToggle={toggleMetric}
                onReorder={reorderMetric}
              />
            ) : (
              'Actions'
            )}
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {posts.map((post, index) => (
          <TopPostRow
            key={post.id}
            post={post}
            rank={rankOffset + index + 1}
            channelId={channelId}
            canCreatePosts={canCreatePosts}
            metricColumns={visibleMetrics.map((m) => m.type)}
          />
        ))}
      </Table.Body>
    </Table>
  )
}

export function TopPosts({
  channelId,
  canCreatePosts = true,
  topPosts,
  loading,
  refreshing = false,
  error,
  page,
  perPage,
  totalCount,
  onPageChange,
  sortBy,
  sortDirection,
  onSortChange,
  startDate,
  endDate,
  comparisonStartDate,
  comparisonEndDate,
  selectedDateRange,
  hasNeverSynced,
  period = 'primary',
  onPeriodChange,
  comparisonLabel,
  comparisonLabelShort,
  hasTagFilter,
  onClearTagFilter,
  hasPostTypeFilter,
  onClearPostTypeFilter,
  onClearAllFilters,
}: TopPostsProps): JSX.Element {
  const totalPages = Math.ceil(totalCount / perPage)
  const rankOffset = (page - 1) * perPage

  const metrics = useMemo(
    () =>
      topPosts.flatMap(
        (p) =>
          p.metrics?.map((m) => ({ type: m.type, displayName: m.name })) ?? [],
      ),
    [topPosts],
  )
  const {
    availableMetrics,
    visibleMetrics,
    selectedMetrics,
    toggleMetric,
    reorderMetric,
    showColumnSelector,
  } = useColumnVisibility({
    availableMetrics: metrics,
    scopeId: channelId,
  })

  // The subtitle shows the date range of whichever period is in view.
  const subtitle =
    period === 'comparison' && comparisonStartDate && comparisonEndDate
      ? formatDateRange(comparisonStartDate, comparisonEndDate)
      : formatDateRange(startDate, endDate)

  const showPeriodToggle = Boolean(
    comparisonLabel &&
    onPeriodChange &&
    comparisonStartDate &&
    comparisonEndDate,
  )

  const renderPeriodToggle = (comparisonText: string): JSX.Element => (
    <ToggleGroup
      type="single"
      size="medium"
      className={sharedStyles.insightsToggle}
      value={period}
      onChange={(value): void => {
        if (value === 'primary' || value === 'comparison') {
          onPeriodChange?.(value)
        }
      }}
      aria-label="Posts period"
    >
      <ToggleGroup.Item value="primary">This Period</ToggleGroup.Item>
      <ToggleGroup.Item value="comparison">{comparisonText}</ToggleGroup.Item>
    </ToggleGroup>
  )

  const header = (
    <SectionCard.Header>
      <SectionCard.Title as="h3">Performance per Post</SectionCard.Title>
      <SectionCard.Actions>
        {showPeriodToggle && comparisonLabel && (
          <span className={styles.periodToggleActions}>
            {renderPeriodToggle(comparisonLabel)}
          </span>
        )}
      </SectionCard.Actions>
      <SectionCard.Subtitle>{subtitle}</SectionCard.Subtitle>
    </SectionCard.Header>
  )

  // On narrow cards the period toggle moves out of the header into its own
  // full-width row below it, using the abbreviated comparison label so the two
  // segments fit on one line.
  const narrowPeriodToggle =
    showPeriodToggle && comparisonLabel ? (
      <div className={styles.periodToggleRow}>
        {renderPeriodToggle(comparisonLabelShort ?? comparisonLabel)}
      </div>
    ) : null

  if (error) {
    return (
      <SectionCard>
        {header}
        {narrowPeriodToggle}
        <ChartErrorState />
      </SectionCard>
    )
  }

  return (
    <SectionCard>
      {header}
      {narrowPeriodToggle}
      <div
        className={clsx(styles.content, refreshing && styles.refreshing)}
        aria-busy={refreshing}
      >
        {loading && topPosts.length === 0 ? (
          <TopPostsTableSkeleton rowCount={perPage} columnCount={4} />
        ) : topPosts.length > 0 ? (
          <TopPostsTable
            posts={topPosts}
            channelId={channelId}
            canCreatePosts={canCreatePosts}
            rankOffset={rankOffset}
            totalCount={totalCount}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
            visibleMetrics={visibleMetrics}
            availableMetrics={availableMetrics}
            selectedMetrics={selectedMetrics}
            toggleMetric={toggleMetric}
            reorderMetric={reorderMetric}
            showColumnSelector={showColumnSelector}
          />
        ) : hasNeverSynced ? (
          <div className={styles.emptyStateCard}>
            <NeverSyncedEmptyState />
          </div>
        ) : (
          <div className={styles.emptyStateCard}>
            <NoDataEmptyState
              description={getNoPostsDescription(selectedDateRange)}
              showSuggestion
              onClearTagFilter={hasTagFilter ? onClearTagFilter : undefined}
              onClearPostTypeFilter={
                hasPostTypeFilter ? onClearPostTypeFilter : undefined
              }
              onClearAllFilters={onClearAllFilters}
            />
          </div>
        )}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <PaginationControls
              page={page}
              totalPages={totalPages}
              onPageChange={onPageChange}
              loading={loading}
            />
          </div>
        )}
      </div>
    </SectionCard>
  )
}
